import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, notification } from 'antd';
import { ReloadOutlined, WarningFilled } from '@ant-design/icons';
import { Default } from '../../../layouts';
import PageTitle from '../../../components/PageTitle';
import SaleReportOrdersGrid from '../../../components/sale-report-orders/SaleReportOrdersGrid';
import {
  getMatviewStatus,
  triggerMatviewRefresh,
} from '../../../services/orders';

const SaleReportOrders = () => {
  const { t } = useTranslation();

  const [matviewStatus, setMatviewStatus] = useState({
    lastRefreshedAt: null,
    refreshing: false,
  });
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(() => {
      getMatviewStatus()
        .then((status) => {
          setMatviewStatus(status);
          if (!status.refreshing) {
            stopPolling();
            notification.success({
              message: t('components.salesReport.matview.refreshDone'),
            });
          }
        })
        .catch(stopPolling);
    }, 3000);
  }, [stopPolling, t]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const fetchStatus = useCallback(() => {
    getMatviewStatus()
      .then((status) => {
        setMatviewStatus(status);
        if (status.refreshing) {
          startPolling();
        }
      })
      .catch(() => {});
  }, [startPolling]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleRefresh = useCallback(() => {
    triggerMatviewRefresh()
      .then(() => {
        setMatviewStatus((prev) => ({ ...prev, refreshing: true }));
        notification.info({
          message: t('components.salesReport.matview.refreshStarted'),
        });
        startPolling();
      })
      .catch(() => {
        notification.error({
          message: t('components.salesReport.matview.refreshError'),
        });
      });
  }, [startPolling, t]);

  return (
    <Default>
      <PageTitle
        title={t('pages.reports.salesReportOrders._')}
        subtitle={
          matviewStatus.lastRefreshedAt
            ? `${t('components.salesReport.matview.generatedAt')}: ${new Date(
                matviewStatus.lastRefreshedAt,
              ).toLocaleString()}`
            : null
        }
      />
      <Alert
        message={
          <span>
            <WarningFilled style={{ color: '#ff4d4f', marginRight: 6 }} />
            {t('components.salesReport.matview.warningPrefix')}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                border: '1px solid #d9d9d9',
                borderRadius: 4,
                padding: '0px 7px',
                margin: '0 4px',
                background: '#fff',
              }}
            >
              <ReloadOutlined />
              {t('components.salesReport.matview.refresh')}
            </span>
            {t('components.salesReport.matview.warningPrefix2')}
          </span>
        }
        type="error"
        style={{ marginBottom: 16 }}
      />
      <SaleReportOrdersGrid
        matviewStatus={matviewStatus}
        onRefresh={handleRefresh}
      />
    </Default>
  );
};

export default SaleReportOrders;
